import {
  BoxProps,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@elkaso-app/web-design';
import { SupplierProfileDto, useUpdateSupplierProfileNotificationsApi } from 'apis/supplier-profiles';
import { MultiValueInput } from 'components/multi-value-input';
import React, { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;
const PHONE_PATTERN = /^[+][0-9]{8,12}$/;

const validateEmailItem = (item: string) => (EMAIL_PATTERN.test(item) ? undefined : 'Enter a valid email address');
const validatePhoneItem = (item: string) =>
  PHONE_PATTERN.test(item) ? undefined : 'Enter a valid number in international format, e.g. +971501234567';

type TInputs = {
  mainEmail: string;
  cc: string[];
  bcc: string[];
  whatsappNumbers: string[];
};

const defaultValues: TInputs = {
  mainEmail: '',
  cc: [],
  bcc: [],
  whatsappNumbers: [],
};

export interface INotificationSettingsModalCell extends BoxProps {
  trigger: JSX.Element;
  profile: SupplierProfileDto;
}

export const NotificationSettingsModal = ({ trigger, profile }: INotificationSettingsModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate: updateNotifications, isLoading } = useUpdateSupplierProfileNotificationsApi();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TInputs>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { ...defaultValues },
  });

  const ccValue = useWatch({ control, name: 'cc' });
  const bccValue = useWatch({ control, name: 'bcc' });

  useEffect(() => {
    if (!isOpen) return;

    reset({
      mainEmail: profile.emailConfig?.to ?? '',
      cc: profile.emailConfig?.cc ?? [],
      bcc: profile.emailConfig?.bcc ?? [],
      whatsappNumbers: profile.whatsappNumbers ?? [],
    });
  }, [isOpen, profile]);

  const handleClose = () => {
    reset({ ...defaultValues });
    onClose();
  };

  const onSubmit = (values: TInputs) => {
    const mainEmail = values.mainEmail.trim();

    updateNotifications(
      {
        id: profile.id,
        body: {
          emailConfig: mainEmail
            ? {
                to: mainEmail,
                ...(values.cc.length ? { cc: values.cc } : {}),
                ...(values.bcc.length ? { bcc: values.bcc } : {}),
              }
            : null,
          whatsappNumbers: values.whatsappNumbers,
        },
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={handleClose} size={{ base: 'full', md: 'lg' }} scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent as='form' onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Notification Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing='md'>
              <Text fontWeight='semibold'>Order emails</Text>

              <FormControl isInvalid={!!errors.mainEmail}>
                <FormLabel>Main email (To)</FormLabel>
                <Input
                  type='email'
                  placeholder='e.g. orders@supplier.com'
                  {...register('mainEmail', {
                    validate: (value) => {
                      const trimmed = value.trim();
                      if (!trimmed && (ccValue.length || bccValue.length))
                        return 'Main email is required when CC or BCC emails are set';
                      if (trimmed && !EMAIL_PATTERN.test(trimmed)) return 'Enter a valid email address';
                      return true;
                    },
                  })}
                />
                <FormErrorMessage>{errors.mainEmail?.message}</FormErrorMessage>
              </FormControl>

              <Controller
                control={control}
                name='cc'
                render={({ field: { onChange, value } }) => (
                  <FormControl>
                    <FormLabel>CC emails</FormLabel>
                    <MultiValueInput
                      value={value}
                      onChange={onChange}
                      validate={validateEmailItem}
                      placeholder='Type an email and press Enter'
                      inputAriaLabel='cc-emails-input'
                    />
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name='bcc'
                render={({ field: { onChange, value } }) => (
                  <FormControl>
                    <FormLabel>BCC emails</FormLabel>
                    <MultiValueInput
                      value={value}
                      onChange={onChange}
                      validate={validateEmailItem}
                      placeholder='Type an email and press Enter'
                      inputAriaLabel='bcc-emails-input'
                    />
                  </FormControl>
                )}
              />

              <Text fontWeight='semibold'>WhatsApp</Text>

              <Controller
                control={control}
                name='whatsappNumbers'
                render={({ field: { onChange, value } }) => (
                  <FormControl>
                    <FormLabel>WhatsApp numbers</FormLabel>
                    <MultiValueInput
                      value={value}
                      onChange={onChange}
                      validate={validatePhoneItem}
                      placeholder='e.g. +971501234567'
                      inputAriaLabel='whatsapp-numbers-input'
                    />
                  </FormControl>
                )}
              />
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack>
              <Button variant='outline' colorScheme='red' onClick={handleClose}>
                Cancel
              </Button>
              <Button variant='solid' colorScheme='red' type='submit' isLoading={isLoading}>
                Save
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
